import { Controller } from "@hotwired/stimulus"

// Rich-text editor for the email campaign body (#550, follow-up to #528).
//
// Same Trix-bridge pattern as the event regulation editor (rules_editor, #542):
// Trix edits a private source <input>; on every change we mirror its HTML into
// the submitted hidden field (email_campaign[html_body]). The server-side
// EmailCampaign::HtmlSanitizer (#533) stays the single security authority — this
// controller is UX fidelity only and never relaxes the allowlist.
//
// Differences from rules_editor:
//   * The email allowlist (#533) is WIDER than the event-rules allowlist — it
//     keeps h1..h6, links, strike (<del>), code (<pre>) and images. So this
//     editor keeps every default Trix tool (the .campaign-editor CSS does NOT
//     hide link/strike/code/file-tools) and only normalizes Trix's default
//     block <div> into <p> (email-standard paragraph). Headings are NOT
//     downgraded — <h1> survives the email allowlist.
//   * Images: the trix-editor carries data-direct-upload-url +
//     data-blob-url-template, so @rails/actiontext uploads an attachment via
//     Active Storage direct upload (browser -> S3) and embeds an OWN-HOST blob
//     URL (rails_service_blob_url). That URL is permanent + public + same-host,
//     so it survives the sanitizer's host filter and never expires in the
//     delivered email (the #529 note: never embed the expiring signed S3 URL).
//     Non-image attachments are rejected (an email body only renders <img>).
//   * Merge tags: the legend buttons (interactive mode) dispatch insertTag,
//     which drops the exact {{tag}} token at the Trix cursor.
export default class extends Controller {
  static targets = ["editor", "source", "output"]

  connect() {
    this._onInitialize = () => this.seed()
    this._onChange = () => this.sync()
    this._onFileAccept = (event) => this.guardAttachment(event)

    this.editorTarget.addEventListener("trix-initialize", this._onInitialize)
    this.editorTarget.addEventListener("trix-change", this._onChange)
    this.editorTarget.addEventListener("trix-file-accept", this._onFileAccept)

    // Trix may have initialized before this controller connected (turbo cache,
    // re-render). Seed immediately in that case.
    if (this.editorTarget.editor) this.seed()
  }

  disconnect() {
    this.editorTarget.removeEventListener("trix-initialize", this._onInitialize)
    this.editorTarget.removeEventListener("trix-change", this._onChange)
    this.editorTarget.removeEventListener("trix-file-accept", this._onFileAccept)
  }

  // Load the stored html_body (held in the submitted field's initial value) into
  // Trix exactly once, then mirror it straight back so a no-edit save is a no-op.
  seed() {
    if (this._seeded || !this.editorTarget.editor) return
    this._seeded = true
    this.editorTarget.editor.loadHTML(this.outputTarget.value || "")
    this.sync()
  }

  // Mirror Trix's serialized output (in the source input) into the submitted
  // field, normalized for email.
  sync() {
    this.outputTarget.value = this.normalize(this.sourceTarget.value)
  }

  // Insert the exact {{tag}} merge token at the current Trix cursor. Called from
  // the merge-tag legend buttons (data-email-campaign-editor-tag-param). The tag
  // is a fixed identifier from the server-rendered legend, never user input.
  insertTag(event) {
    const tag = event.params.tag
    if (!tag || !this.editorTarget.editor) return

    this.editorTarget.editor.insertString(`{{${tag}}}`)
    this.editorTarget.focus()
    this.sync()
  }

  // An email body can only render <img>; reject any non-image attachment before
  // Trix uploads it (a PDF/zip would round-trip to a same-host file link the
  // recipient cannot meaningfully use in an email).
  guardAttachment(event) {
    const file = event.file
    if (!file || !file.type.startsWith("image/")) event.preventDefault()
  }

  // Trix output -> email-friendly HTML. Trix emits <div> for its default block;
  // <p> is the email-standard paragraph. Everything else Trix produces
  // (h1..h6 / strong / em / del / a / blockquote / pre / ul / ol / li / figure /
  // img) is already in the email allowlist (#533) and rides through untouched;
  // anything outside it is stripped server-side regardless.
  normalize(html) {
    const template = document.createElement("template")
    template.innerHTML = html || ""
    template.content.querySelectorAll("div").forEach((node) => this.rename(node, "p"))
    return template.innerHTML.trim()
  }

  rename(node, tag) {
    const replacement = document.createElement(tag)
    while (node.firstChild) replacement.appendChild(node.firstChild)
    node.replaceWith(replacement)
  }
}
